import { Image, Maximize2, Move, Paintbrush } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import React from 'react'
import Moveable from 'react-moveable'
import { Design_ } from 'src/api/db/apiDesigns'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { getFilterStyle } from 'src/shared/components/Design/utils'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'src/shared/components/ui/toggle-group'
import { TabNavigation } from 'src/shared/components/TabNavigation'
import { PreviewTab } from './DesignArea/components/PreviewTab'

export const DesignArea = ({
  width,
  height,
  design_,
  setDesign_,
  quote_sub_item_id,
  quote_item_id,
  product_name,
  mockup_urls,
  decoration_name,
  onDeleteMockup,
}: {
  width: number
  height: number
  design_: Partial<Design_>
  setDesign_: (
    design: Partial<Design_> | ((prev: Partial<Design_>) => Partial<Design_>)
  ) => void
  quote_sub_item_id?: number
  quote_item_id?: number
  product_name?: string
  mockup_urls?: string[] | null
  decoration_name?: string | null
  onDeleteMockup?: (url: string) => void
}) => {
  if (!design_) return null

  const { t } = useLocalization()
  const [isSelected, setIsSelected] = useState(true)
  const [editMode, setEditMode] = useState<'resize' | 'warp'>('resize')
  const [designMode, setDesignMode] = useState<'edit' | 'preview'>('edit')
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const productImageRef = useRef<HTMLImageElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const lastDragPositionRef = useRef<{ left: number; top: number } | null>(null)

  // Memoize filter style to prevent unnecessary recalculations
  const filterStyle = useMemo(
    () => getFilterStyle(design_ || {}),
    [
      design_?.brightness,
      design_?.saturation,
      design_?.hue,
      design_?.colorFilter,
    ]
  )

  // Smooth drag handler - updates DOM directly for immediate visual feedback
  const handleDrag = ({ left, top }: any) => {
    if (targetRef.current) {
      // Store the last drag position
      lastDragPositionRef.current = { left, top }

      // Cancel any pending animation frame
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }

      // Update DOM directly for smooth visual feedback
      targetRef.current.style.left = `${left}px`
      targetRef.current.style.top = `${top}px`

      // Throttle state updates using requestAnimationFrame
      rafRef.current = requestAnimationFrame(() => {
        setDesign_((prev) => ({ ...prev, x: left, y: top }))
        rafRef.current = null
      })
    }
  }

  // Update state on drag end to ensure final position is persisted
  const handleDragEnd = (e: any) => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    const left =
      lastDragPositionRef.current?.left ??
      (targetRef.current
        ? parseFloat(targetRef.current.style.left)
        : undefined) ??
      e?.left

    const top =
      lastDragPositionRef.current?.top ??
      (targetRef.current
        ? parseFloat(targetRef.current.style.top)
        : undefined) ??
      e?.top

    if (left !== undefined && top !== undefined) {
      setDesign_((prev) => ({ ...prev, x: left, y: top }))
    }
  }

  // Warp handler - stores the transform matrix
  const handleWarp = (e: any) => {
    if (targetRef.current) {
      targetRef.current.style.transform = e.transform
      setDesign_((prev) => ({ ...prev, warp_transform: e.transform }))
    }
  }

  // Initialize warp transform from saved state when component mounts or warp_transform changes
  useEffect(() => {
    if (!targetRef.current) return

    const rotationTransform = `rotateX(${design_.rotation_x || 0}deg) rotateY(${
      design_.rotation_y || 0
    }deg) rotateZ(${design_.rotation_z || 0}deg)`

    if (editMode === 'warp' && design_.warp_transform) {
      targetRef.current.style.transform = design_.warp_transform
    } else if (editMode === 'resize' && !design_.warp_transform) {
      targetRef.current.style.transform = rotationTransform
    }
  }, [
    design_.warp_transform,
    editMode,
    design_.rotation_x,
    design_.rotation_y,
    design_.rotation_z,
  ])

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  // Click outside listener to deselect
  useEffect(() => {
    if (!isSelected) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      const moveableSelectors = [
        '.moveable-control',
        '.moveable-line',
        '.moveable-control-box',
        '.moveable',
      ]

      if (
        moveableSelectors.some((sel) => target.closest(sel)) ||
        target.closest('[role="group"]') ||
        targetRef.current?.contains(target) ||
        imageRef.current?.contains(target)
      ) {
        return
      }

      if (
        target === productImageRef.current ||
        target === containerRef.current ||
        !containerRef.current?.contains(target)
      ) {
        setIsSelected(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside, true)
    return () =>
      document.removeEventListener('mousedown', handleClickOutside, true)
  }, [isSelected])

  useEffect(() => {
    setIsSelected(true)
  }, [design_])

  return (
    <Column className="gap-2">
      {/* Edit Mode Toggle Overlay */}
      <Row className="items-center justify-center">
        <TabNavigation
          ghost={true}
          tabs={[
            {
              id: 'edit',
              label: 'Edit',
              icon: <Paintbrush className="w-4 h-4 mr-1.5" />,
            },
            {
              id: 'preview',
              label:
                'Preview' +
                (mockup_urls?.length ? ` (${mockup_urls.length})` : ''),
              icon: <Image className="w-4 h-4 mr-1.5" />,
            },
          ]}
          activeTab={designMode}
          onTabChange={(tab) => setDesignMode(tab as 'edit' | 'preview')}
        />
      </Row>

      {designMode === 'edit' ? (
        <Row className="gap-2 w-fit">
          <div
            ref={containerRef}
            className="relative overflow-hidden rounded-lg bg-white/70 border border-gray-200/80"
            style={{ width: `${width}px`, height: `${height}px` }}
            onClick={(e: React.MouseEvent) => {
              const target = e.target as HTMLElement
              if (
                targetRef.current?.contains(target) ||
                imageRef.current?.contains(target) ||
                target.closest('[role="group"]')
              ) {
                return
              }
              setIsSelected(false)
            }}
          >
            <ToggleGroup
              type="single"
              value={editMode}
              onValueChange={(value) => {
                if (value !== 'resize' && value !== 'warp') return
                setEditMode(value)

                if (!targetRef.current) return

                const rotationTransform = `rotateX(${
                  design_.rotation_x || 0
                }deg) rotateY(${design_.rotation_y || 0}deg) rotateZ(${
                  design_.rotation_z || 0
                }deg)`

                if (value === 'resize' && design_.warp_transform) {
                  setDesign_((prev) => ({ ...prev, warp_transform: undefined }))
                  targetRef.current.style.transform = rotationTransform
                } else if (value === 'warp' && design_.warp_transform) {
                  targetRef.current.style.transform = design_.warp_transform
                }
              }}
              variant="outline"
              className="shadow-sm pointer-events-auto absolute bottom-2 left-1/2 -translate-x-1/2 z-10"
              onClick={(e) => e.stopPropagation()}
              size="sm"
            >
              <ToggleGroupItem
                value="resize"
                aria-label={t('Resize mode', 'Storleksläge')}
                className="w-fit"
              >
                <Maximize2 className="w-4 h-4 mr-1.5" />
                <span className="text-xs">{t('Resize', 'Storlek')}</span>
              </ToggleGroupItem>

              <ToggleGroupItem
                value="warp"
                aria-label={t('Warp mode', 'Perspektivläge')}
                className="w-fit"
              >
                <Move className="w-4 h-4 mr-1.5" />
                <span className="text-xs">{t('Warp', 'Perspektiv')}</span>
              </ToggleGroupItem>
            </ToggleGroup>

            <img
              ref={productImageRef}
              src={design_.image_url || ''}
              alt={t('Product', 'Produkt')}
              className="absolute inset-0 w-full h-full object-contain"
            />

            {design_.design_url && (
              <>
                <div
                  ref={targetRef}
                  className={`absolute ${
                    isSelected ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
                  }`}
                  style={{
                    transform: design_.warp_transform
                      ? design_.warp_transform
                      : `rotateX(${design_.rotation_x || 0}deg) rotateY(${
                          design_.rotation_y || 0
                        }deg) rotateZ(${design_.rotation_z || 0}deg)`,
                    left: `${design_.x || 0}px`,
                    top: `${design_.y || 0}px`,
                    width: `${design_.w || 100}px`,
                    height: `${design_.h || 100}px`,
                    zIndex: 10,
                    opacity: design_.opacity || 1,
                  }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    setIsSelected(true)
                  }}
                >
                  <img
                    ref={imageRef}
                    src={design_.design_url}
                    alt={t('Logo', 'Logotyp')}
                    className="w-full h-full object-contain"
                    style={{ filter: filterStyle }}
                    draggable={false}
                  />
                </div>

                <Moveable
                  target={targetRef.current}
                  container={containerRef.current}
                  draggable={true}
                  resizable={isSelected && editMode === 'resize'}
                  warpable={isSelected && editMode === 'warp'}
                  rotatable={isSelected && editMode === 'resize'}
                  keepRatio={editMode === 'resize'}
                  origin={false}
                  edge={false}
                  hideDefaultLines={!isSelected}
                  onDragStart={() => {
                    setIsSelected(true)
                  }}
                  onDrag={handleDrag}
                  onDragEnd={handleDragEnd}
                  onResizeStart={() => setIsSelected(true)}
                  onResize={(e) =>
                    setDesign_((prev) => ({
                      ...prev,
                      w: e.width,
                      h: e.height,
                    }))
                  }
                  onRotateStart={() => {
                    setIsSelected(true)
                  }}
                  onRotate={(e) =>
                    setDesign_((prev) => ({
                      ...prev,
                      rotation_z: e.rotation,
                    }))
                  }
                  onWarpStart={() => {
                    setIsSelected(true)
                  }}
                  onWarp={handleWarp}
                />
              </>
            )}
          </div>
        </Row>
      ) : (
        <PreviewTab
          design_={design_}
          quote_sub_item_id={quote_sub_item_id}
          quote_item_id={quote_item_id}
          product_name={product_name}
          mockup_urls={mockup_urls}
          decoration_name={decoration_name}
          onDeleteMockup={onDeleteMockup}
        />
      )}
    </Column>
  )
}
